import axios from "axios";
import type { TarotCard } from "@variants/seer/master-s/shared/types/tarot-card";
import { getMasterSApiLang, getMasterSLocale } from "@variants/seer/master-s/shared/utils/i18n";

const unwrapData = <T = any>(res: any): T => {
	const body = res?.data ?? res;
	return (body?.data ?? body) as T;
};

/** 登录用户 token：与站点主站登录态一致 */
export function getMasterSToken() {
	if (!import.meta.client) return "";
	const token = localStorage.getItem("token") || "";
	if (!token) {
		localStorage.removeItem("master_s_uid");
		localStorage.removeItem("master_s_token");
	}
	return token;
}

/** 同步读取已缓存的登录用户 uid（需先 resolveMasterSUid） */
export function getMasterSUid() {
	if (!import.meta.client) return "";
	if (!getMasterSToken()) return "";
	return localStorage.getItem("master_s_uid") || "";
}

/**
 * 从登录用户信息解析 MasterS WebSocket 所需 uid，并缓存到 localStorage。
 * 字段优先级与站内其它模块一致：user_id → uid → id → socket_uid
 */
export async function resolveMasterSUid(force = false) {
	if (!import.meta.client) return "";
	if (!getMasterSToken()) {
		localStorage.removeItem("master_s_uid");
		return "";
	}
	if (!force) {
		const cached = localStorage.getItem("master_s_uid") || "";
		if (cached) return cached;
	}
	try {
		const { getUserInfo } = await import("./user");
		const res = await getUserInfo();
		const uid = String(
			res?.data?.user_id || res?.data?.uid || res?.data?.id || res?.data?.socket_uid || ""
		);
		if (uid) localStorage.setItem("master_s_uid", uid);
		else localStorage.removeItem("master_s_uid");
		return uid;
	} catch (error) {
		console.error("MasterS resolve uid failed", error);
		return localStorage.getItem("master_s_uid") || "";
	}
}

function toFormData(params: Record<string, string | number>, withToken = false) {
	const formData = new FormData();
	Object.entries(params).forEach(([key, value]) => {
		formData.append(key, String(value));
	});
	if (withToken) {
		const token = getMasterSToken();
		if (token) {
			formData.append("YQW-Token", token);
			formData.append("Authorization", token);
		}
	}
	return formData;
}

function createMasterSRequest() {
	const config = useRuntimeConfig();
	const baseURL =
		(config.public.masterSApiBase as string) ||
		(config.public.env === "production"
			? "https://h5.seerq.io"
			: "https://sandbox-h5.seeronnet.com");

	const request = axios.create({
		baseURL,
		timeout: 30000,
	});

	request.interceptors.request.use(cfg => {
		const token = getMasterSToken();
		const host = baseURL.replace(/^https?:\/\//, "").replace(/\/$/, "");

		cfg.headers.set("Version", "1.0.0");
		cfg.headers.set("Channel", "app_ios_6455938273");
		if (!import.meta.client) {
			cfg.headers.set("Host", host);
		}
		cfg.headers.set("API-Version", "V1");
		cfg.headers.set("App", "Android_Lingji_Dashi");
		cfg.headers.set("Platform", "71");
		cfg.headers.set("AppID", "6455938273");
		const lang = getMasterSApiLang();
		cfg.headers.set("Lang", lang);
		cfg.headers.set("lang", lang);
		cfg.headers.set("Accept-Language", getMasterSLocale() === "en" ? "en-US" : "zh-TW");
		cfg.headers.set(
			"timezone",
			Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Shanghai"
		);

		if (token) {
			cfg.headers.set("YQW-Token", token);
			cfg.headers.set("Authorization", token);
		}

		return cfg;
	});

	request.interceptors.response.use(
		response => {
			const data = response.data;
			if (data?.code === 0 || data?.code === 200 || data?.code === 1 || data?.success) {
				return data;
			}
			return Promise.reject(new Error(data?.msg || data?.message || "请求失败"));
		},
		error => Promise.reject(error)
	);

	return request;
}

function useMasterSRequest() {
	return createMasterSRequest();
}

async function postForm<T = any>(url: string, params: Record<string, string | number>) {
	const token = getMasterSToken();
	const res = await useMasterSRequest().post(url, toFormData(params, true), {
		headers: {
			"Content-Type": "multipart/form-data",
			...(token ? { "YQW-Token": token, Authorization: token } : {}),
		},
	});
	return unwrapData<T>(res);
}

export async function getActionVideoUrl() {
	const res = await useMasterSRequest().get("/digital_human/action_video_url", { params: {} });
	return unwrapData<{ video_urls: string[]; audio_urls: string[]; image_urls: string[] }>(res);
}

export async function setUserCharacter(characterType: number) {
	return postForm("/digital_human/user_character", { character_type: characterType });
}

export async function addMasterSModel(modelIds: number) {
	return postForm("/digital_human/user_add_model", { model_ids: modelIds });
}

export async function setMasterSModel(modelId: number) {
	return postForm<{ greeting_text: string; audio_url: string }>("/digital_human/set_model", {
		model_id: modelId,
	});
}

export async function getQuestionList(type: number) {
	const res = await useMasterSRequest().get("/digital_human/question_list", { params: { type } });
	return unwrapData<string[]>(res);
}

export async function deleteChatHistory(type: number) {
	return postForm("/digital_human/delete_chat_history", { type });
}

export async function getTarotCards() {
	const res = await useMasterSRequest().get("/v1/live/tarot/draw", { params: {} });
	const data = unwrapData<{ list?: TarotCard[] } | TarotCard[]>(res);
	if (Array.isArray(data)) return data;
	return data.list ?? [];
}

export type AlonePayParams = {
	tag: string;
	amount: string | number;
	digital_human_v: string | number;
	pay_model: string | number;
	platform?: string | number;
	channel?: string;
	return_url?: string;
};

export type AlonePayResult = {
	third_order_id: string;
	out_trade_no?: string;
	area_payment: Array<{
		name: string;
		list: Array<{
			type: string;
			name: string;
			url?: string;
			icon?: string;
			[key: string]: any;
		}>;
	}>;
	amount?: string;
	final_amount?: string;
	currency_symbol?: string;
	iso_amount?: string;
	iso_final_amount?: string;
	[key: string]: any;
};

/** MasterS 独立支付下单（拉起收银台前调用） */
export async function alonePay(params: AlonePayParams) {
	return postForm<AlonePayResult>("/v1/order/alone_pay", {
		tag: params.tag,
		amount: params.amount,
		digital_human_v: params.digital_human_v,
		pay_model: params.pay_model,
		...(params.platform ? { platform: params.platform } : {}),
		...(params.channel ? { channel: params.channel } : {}),
		...(params.return_url ? { return_url: params.return_url } : {}),
	});
}

export async function uploadImage(blob: Blob, filename = "image.png") {
	const token = getMasterSToken();
	const formData = new FormData();
	formData.append("Filedata", blob, filename);
	if (token) {
		formData.append("YQW-Token", token);
		formData.append("Authorization", token);
	}

	const res = await useMasterSRequest().post("/v1/upload/image", formData, {
		headers: {
			"Content-Type": "multipart/form-data",
			...(token ? { "YQW-Token": token, Authorization: token } : {}),
		},
	});
	const data = unwrapData<{ image_url?: string; url?: string; image?: string }>(res);
	return data.image_url || data.url || data.image || "";
}
